"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import {
  HOOK_FRAMES,
  type HookType,
  hooksFor,
} from "@/lib/tool-kit/essay/hooks";

/** English strings. A translated content file overrides these through `ui`. */
export const ESSAY_HOOK_GENERATOR_STRINGS = {
  question: "Question",
  statistic: "Surprising statistic",
  quotation: "Quotation",
  anecdote: "Anecdote",
  bold: "Bold statement",
  yourEssayTopic: "Your essay topic",
  topicPlaceholder: "e.g. school uniforms, renewable energy, social media",
  hookType: "Hook type",
  generateHooks: "Generate Hooks",
  templateOpenersHeading:
    "Template openers — adapt the wording and fill in any bracketed placeholders with real details",
  copied: "Copied!",
  copy: "Copy",
  hookFrames: HOOK_FRAMES,
} as const;

export default function EssayHookGeneratorWidget() {
  const [topic, setTopic] = useState("");
  const [hookType, setHookType] = useState<HookType>("question");
  const [hooks, setHooks] = useState<string[]>([]);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const strings = useToolUi(ESSAY_HOOK_GENERATOR_STRINGS);

  const hookTypes: { value: HookType; label: string }[] = [
    { value: "question", label: strings.question },
    { value: "statistic", label: strings.statistic },
    { value: "quotation", label: strings.quotation },
    { value: "anecdote", label: strings.anecdote },
    { value: "bold", label: strings.bold },
  ];

  function handleGenerate() {
    const trimmed = topic.trim();
    if (trimmed.length === 0) {
      return;
    }
    setHooks(hooksFor(trimmed, hookType, strings.hookFrames[hookType]));
    setCopiedIndex(null);
  }

  function handleCopy(hook: string, index: number) {
    writeToClipboard(hook).then((copied) => {
      if (!copied) {
        return;
      }

      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    });
  }

  return (
    <>
      {/* Topic */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="hook-topic"
        >
          {strings.yourEssayTopic}
        </label>
        <input
          className="w-full rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="hook-topic"
          onChange={(e) => setTopic(e.target.value)}
          placeholder={strings.topicPlaceholder}
          type="text"
          value={topic}
        />
      </div>

      {/* Hook type */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.hookType}
        </p>
        <div className="flex flex-wrap gap-3">
          {hookTypes.map((t) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={t.value}
            >
              <input
                checked={hookType === t.value}
                className="accent-primary"
                name="hook-type"
                onChange={() => setHookType(t.value)}
                type="radio"
                value={t.value}
              />
              {t.label}
            </label>
          ))}
        </div>
      </section>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={topic.trim().length === 0}
          onClick={handleGenerate}
          type="button"
        >
          {strings.generateHooks}
        </button>
      </div>

      {/* Results */}
      {hooks.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-medium text-foreground text-sm">
            {strings.templateOpenersHeading}
          </h2>
          <ul className="space-y-3">
            {hooks.map((hook, index) => (
              <li
                className="flex items-start justify-between gap-4 rounded-lg border border-border bg-card p-5"
                key={hook}
              >
                <p className="text-foreground text-sm leading-relaxed">
                  {hook}
                </p>
                <button
                  className="shrink-0 rounded-lg border border-border bg-card px-3 py-1.5 font-medium text-foreground text-xs transition-colors hover:bg-muted"
                  onClick={() => handleCopy(hook, index)}
                  type="button"
                >
                  {copiedIndex === index ? strings.copied : strings.copy}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
